import { getCachedConnectorConnections } from "@mirage/service-connectors";
import { get as getSetting } from "@mirage/service-settings/index";
import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import { LOCAL_FILE_CONNECTOR } from "@mirage/shared/connectors";
import { filterableContentTypes } from "@mirage/shared/content-type/content-types";
import {
  getLastUpdatedObjects,
  LastUpdatedFilterKeys,
} from "@mirage/shared/last-updated/last-updated";
import type {
  ConnectorFilter,
  SearchFilter,
} from "@mirage/shared/search/search-filters";
import {
  contentTypeToSearchFilter,
  extractTrailingSearchFilter,
  getFilterBinding,
  lastUpdatedToSearchFilter,
  peopleRecommendationToSearchFilter,
  SearchFilterKeyword,
  SearchFilterType,
  toFilterBinding,
} from "@mirage/shared/search/search-filters";
import Sentry from "@mirage/shared/sentry";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import debounce from "lodash.debounce";
import * as rx from "rxjs";
import * as op from "rxjs/operators";

import type { ConnectorConnection } from "@mirage/service-dbx-api";
import { getSourceLimit } from "@mirage/service-typeahead-search/service/utils/source-helpers";
import type { PeopleRecommendation } from "@mirage/shared/search/people-recommendations";
import * as sourceScore from "../scoring/source-score";
import { CacheKey } from "../typeahead-cache";

export const raw: typeahead.TaggedSearch = (query, mode, options) => {
  const sourceLimit = getSourceLimit(
    typeahead.ResultType.SearchFilter,
    query,
    mode,
    options.config,
    options.grossLimit,
    options.sectionLimit,
  );

  return rx.from(_internalSearch(query, options)).pipe(
    op.map((results) => {
      return results
        .map((result) => {
          const taggedResult = primitives.searchFilter(result.id, result);
          return sourceScore.score(query, options.config)(taggedResult);
        })
        .sort((a, b) => b.score! - a.score!)
        .slice(0, sourceLimit);
    }),
    op.mergeMap((results) => rx.from(results)),
  );
};

// exported for testing only
// Consider moving this method else if the need arises
export function connectionToSearchFilter(
  connection: ConnectorConnection,
): ConnectorFilter | undefined {
  if (
    !connection?.connector?.id_attrs?.type ||
    !connection?.connector?.branding?.display_name
  ) {
    return;
  }

  return {
    id: connection?.connector?.id_attrs?.type,
    type: SearchFilterType.Connector,
    parameters: {
      connectorId: connection.connector.id_attrs?.type,
      displayName: connection.connector.branding?.display_name,
      iconLightSrc: connection.connector.branding?.icon_src,
      iconDarkSrc: connection.connector.branding?.icon_dark_src,
    },
  };
}

export function filterUsing(
  keyword: SearchFilterKeyword,
  filterText: string,
  query: string,
  customConditionsCallback?: (filter: SearchFilter) => boolean,
): (searchFilter: SearchFilter) => boolean {
  return (searchFilter) => {
    const globalConditions =
      isDefined(searchFilter) &&
      getFilterBinding(searchFilter).startsWith(filterText.toLowerCase()) &&
      !query
        .toLowerCase()
        .includes(toFilterBinding(keyword, getFilterBinding(searchFilter)));

    if (typeof customConditionsCallback === "function") {
      return globalConditions && customConditionsCallback(searchFilter);
    }

    return globalConditions;
  };
}

export function sortBy<
  T extends { parameters: Record<K, string> },
  K extends keyof T["parameters"],
>(parameter: K) {
  return (a: T, b: T): number => {
    return a.parameters[parameter].localeCompare(b.parameters[parameter]);
  };
}

export function mapWith<T extends object, R extends SearchFilter>(
  mapTransformer: (typeaheadResult: T) => R | undefined,
) {
  return (typeaheadResult: T): R => mapTransformer(typeaheadResult)!;
}

export function debounceAsync<
  T extends (...args: string[]) => Promise<SearchFilter[]>,
>(func: T, wait: number): (...args: Parameters<T>) => ReturnType<T> {
  let pendingPromises: Array<{
    resolve: (f: SearchFilter[] | PromiseLike<SearchFilter[]>) => void;
    reject: (e: unknown) => void;
  }> = [];

  const debouncedFunc = debounce(async (...args: Parameters<T>) => {
    try {
      const result = await func(...args);
      pendingPromises.forEach(({ resolve }) => resolve(result));
    } catch (error) {
      pendingPromises.forEach(({ reject }) => reject(error));
    } finally {
      pendingPromises = [];
    }
  }, wait);

  return (...args: Parameters<T>) => {
    return new Promise((resolve, reject) => {
      pendingPromises.push({ resolve, reject });
      debouncedFunc(...args);
    }) as ReturnType<T>;
  };
}

export async function _internalSearch(
  query: string,
  { cache }: typeahead.TaggedSearchOptions,
): Promise<SearchFilter[]> {
  const { filterType, query: filterText } = extractTrailingSearchFilter(query);

  if (!filterType) return [];

  try {
    if (filterType === SearchFilterType.Connector) {
      const isLocalFilesEnabled = (await getSetting("localFiles")).enabled;

      const connectors = await getCachedConnectorConnections();

      if (isLocalFilesEnabled) {
        connectors.push({ connector: LOCAL_FILE_CONNECTOR });
      }

      return connectors
        .map(mapWith(connectionToSearchFilter))
        .filter(filterUsing(SearchFilterKeyword.Connector, filterText, query))
        .sort(sortBy("displayName"));
    } else if (filterType === SearchFilterType.ContentType) {
      return filterableContentTypes
        .map(mapWith(contentTypeToSearchFilter))
        .filter(filterUsing(SearchFilterKeyword.ContentType, filterText, query))
        .sort(sortBy("label"));
    } else if (filterType === SearchFilterType.Person) {
      const peopleRecommendations = cache.all(
        CacheKey.PeopleRecommendations,
      ) as PeopleRecommendation[];

      return peopleRecommendations
        .map(mapWith(peopleRecommendationToSearchFilter))
        .filter(filterUsing(SearchFilterKeyword.Person, filterText, query))
        .sort(sortBy("email"));
    } else if (filterType === SearchFilterType.LastUpdated) {
      return getLastUpdatedObjects()
        .map(mapWith(lastUpdatedToSearchFilter))
        .filter(
          filterUsing(
            SearchFilterKeyword.LastUpdated,
            filterText,
            query,
            (filter) =>
              filter.id !== LastUpdatedFilterKeys.Any_Date &&
              filter.id !== LastUpdatedFilterKeys.Custom,
          ),
        )
        .sort(sortBy("title"));
    } else {
      return [];
    }
  } catch (e) {
    Sentry.captureException(e);
    return [];
  }
}

export const search = wrappers.wrapped(SourceId.SearchFilter, raw);
